import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, Clock, User, Calendar, FileText, MessageSquare,
  ArrowRight, Download, Archive, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { TimeOffRecordsPanel } from "@/pages/employee/TimeOffRecords";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  pending_substitute: "Așteaptă confirmarea ta ca înlocuitor",
  pending_manager: "Așteaptă aprobarea ta (Manager)",
  pending_hr: "Așteaptă aprobarea HR",
};

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function TimeOffApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reject, setReject] = useState(null); // {req, stage}
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("pending");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  async function deleteRequest(r) {
    if (!window.confirm("Ștergi definitiv această cerere? Zilele de concediu revin automat la cele disponibile.")) return;
    try {
      await api.delete(`/timeoff/requests/${r.id}`);
      toast.success("Cerere ștearsă — zilele au revenit la cele disponibile");
      setDetail(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/timeoff/requests/pending");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function decide(req, stage, action, cmt = "") {
    setBusy(req.id);
    let url;
    if (stage === "substitute") {
      url = `/timeoff/requests/${req.id}/${action === "approve" ? "substitute-confirm" : "substitute-reject"}`;
    } else if (stage === "cancel") {
      url = `/timeoff/requests/${req.id}/${action === "approve" ? "approve-cancel" : "reject-cancel"}`;
    } else if (stage === "manager") {
      url = `/timeoff/requests/${req.id}/${action === "approve" ? "approve-manager" : "reject-manager"}`;
    } else {
      url = `/timeoff/requests/${req.id}/${action === "approve" ? "approve-hr" : "reject-hr"}`;
    }
    try {
      await api.post(url, { comment: cmt });
      const successMap = {
        cancel_approve: "Anulare confirmată",
        cancel_reject: "Anulare respinsă — cerere rămâne aprobată",
        substitute_approve: "Ai confirmat înlocuirea",
        substitute_reject: "Ai respins înlocuirea — cererea revine la solicitant",
      };
      const key = `${stage}_${action}`;
      toast.success(successMap[key] || (action === "approve" ? "Cerere aprobată" : "Cerere respinsă"));
      setReject(null); setComment("");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(null); }
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Split by stage
  const substituteItems = items.filter(i => i.status === "pending_substitute");
  const managerItems = items.filter(i => i.status === "pending_manager");
  const hrItems = items.filter(i => i.status === "pending_hr");
  const cancelItems = items.filter(i => i.status === "pending_cancellation");

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Aprobări</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2">Cereri concedii & absențe — aprobări</h1>
          <p className="text-ink-muted text-sm">
            Aprobă sau respinge cererile echipei tale (ca Manager Superior) și cele care așteaptă HR.
          </p>
        </div>

        {/* Tabs: De aprobat | Evidență */}
        <div className="flex items-center gap-1 border-b border-neutral-200 mb-8">
          <button data-testid="tab-pending" onClick={() => setTab("pending")}
            className={`px-4 py-2.5 text-[14px] font-medium border-b-2 -mb-px transition-colors ${tab === "pending" ? "border-brand text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}>
            De aprobat{items.length ? ` (${items.length})` : ""}
          </button>
          <button data-testid="tab-records" onClick={() => setTab("records")}
            className={`px-4 py-2.5 text-[14px] font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === "records" ? "border-brand text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}>
            <Archive className="h-4 w-4" /> Evidență (istoric)
          </button>
        </div>

        {tab === "records" ? (
          <TimeOffRecordsPanel />
        ) : loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white border border-neutral-200 text-ink-muted">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-3" />
            <p className="text-sm">Nu ai cereri de aprobat momentan.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {substituteItems.length > 0 && (
              <Section title="Confirmare înlocuire"
                subtitle="Colegi te-au propus ca înlocuitor — confirmă sau respinge că preiei responsabilitățile"
                items={substituteItems} stage="substitute"
                onDecide={decide} onReject={setReject} onDetail={setDetail} busy={busy} />
            )}
            {managerItems.length > 0 && (
              <Section title="Manager Superior" subtitle="Aprobare de nivel 1"
                items={managerItems} stage="manager"
                onDecide={decide} onReject={setReject} onDetail={setDetail} busy={busy} />
            )}
            {cancelItems.length > 0 && (
              <Section title="Solicitări de anulare / revocare"
                subtitle="Angajații cer anularea unei cereri deja aprobate — confirmă sau respinge"
                items={cancelItems} stage="cancel"
                onDecide={decide} onReject={setReject} onDetail={setDetail} busy={busy} />
            )}
            {hrItems.length > 0 && (
              <Section title="HR" subtitle="Aprobare finală"
                items={hrItems} stage="hr"
                onDecide={decide} onReject={setReject} onDetail={setDetail} busy={busy} />
            )}
          </div>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!reject} onOpenChange={o => !o && setReject(null)}>
        <DialogContent className="">
          <DialogHeader>
            <DialogTitle>Respinge cererea</DialogTitle>
            <DialogDescription>
              Introduci motivul respingerii. Angajatul va primi notificare + email cu motivul.
            </DialogDescription>
          </DialogHeader>
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder={reject?.stage === "substitute" ? "Motiv (opțional)..." : "Motiv respingere..."} rows={4}
            className="w-full border border-neutral-300 p-3 text-sm" />
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setReject(null)}
              className="px-4 py-2 text-sm text-ink-muted">Anulează</button>
            <button
              disabled={busy || (reject?.stage !== "substitute" && !comment.trim())}
              onClick={() => decide(reject.req, reject.stage, "reject", comment)}
              className="px-4 py-2 text-sm bg-red-600 text-white hover:opacity-90">
              {reject?.stage === "substitute" ? "Confirmă respingerea înlocuirii" : "Confirmă respingerea"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.type?.name}</DialogTitle>
                <DialogDescription>
                  {detail.employee?.name} · {fmtDate(detail.start_date)} → {fmtDate(detail.end_date)}
                </DialogDescription>
              </DialogHeader>
              {detail.reason && (
                <div className="mt-2">
                  <div className="text-xs uppercase font-mono text-ink-muted mb-1">Motiv</div>
                  <div className="text-sm text-ink">{detail.reason}</div>
                </div>
              )}
              {detail.substitute && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-mono text-ink-muted mb-1">Înlocuitor propus</div>
                  <div className="text-sm">{detail.substitute.name} — {detail.substitute.job_title}
                    {detail.substitute_confirmed_at && <span className="text-emerald-700"> · ✓ confirmat</span>}
                  </div>
                </div>
              )}
              {detail.client_situation_label && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-mono text-ink-muted mb-1">Situație client</div>
                  <div className="text-sm text-ink">{detail.client_situation_label}</div>
                </div>
              )}
              {detail.documents?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-mono text-ink-muted mb-2">Documente atașate</div>
                  <div className="space-y-1">
                    {detail.documents.map(d => (
                      <a key={d.id}
                        href={`${backendUrl}/api/timeoff/attachments/${detail.id}/${d.id}/download`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center justify-between text-xs bg-neutral-50 p-2 hover:bg-neutral-100">
                        <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{d.filename}</span>
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4">
                <div className="text-xs uppercase font-mono text-ink-muted mb-2">Istoric</div>
                <div className="space-y-2 text-sm">
                  {(detail.history || []).map((h, i) => (
                    <div key={i} className="flex gap-3 border-l-2 border-neutral-200 pl-3 py-1">
                      <div className="text-ink-muted text-xs min-w-[110px]">
                        {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                      </div>
                      <div>
                        <div className="font-medium">{h.by_name} — {h.action}</div>
                        {h.comment && <div className="text-ink-muted text-xs">{h.comment}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {isAdmin && (
                <div className="flex justify-end pt-4 mt-2 border-t border-neutral-200">
                  <button onClick={() => deleteRequest(detail)}
                    data-testid={`approvals-delete-${detail.id}`}
                    className="px-4 py-2 text-sm border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /> Șterge definitiv
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}


function Section({ title, subtitle, items, stage, onDecide, onReject, onDetail, busy }) {
  const approveLabel = stage === "cancel" ? "Confirmă anularea" : stage === "substitute" ? "Confirm înlocuirea" : "Aprobă";
  const rejectLabel = stage === "cancel" ? "Refuză anularea" : stage === "substitute" ? "Respinge înlocuirea" : "Respinge";
  return (
    <div>
      <div className="mb-3">
        <h2 className="font-semibold text-ink text-lg">{title}</h2>
        <p className="text-xs text-ink-muted">{subtitle} · {items.length} cerere(i)</p>
      </div>
      <div className="md:hidden" data-testid={`approvals-cards-${stage}`}>
        {items.map(r => (
          <div key={r.id} className="m-card" data-testid={`approval-card-${r.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0">
                <User className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14.5px] text-ink truncate">{r.employee?.name}</div>
                <div className="text-[12px] text-ink-muted truncate">{r.employee?.job_title}</div>
              </div>
              <span className="pio-badge pio-badge-gray shrink-0">{r.business_days} zile</span>
            </div>
            <div className="m-kv">
              <span className="m-kv-label">Tip</span>
              <span className="m-kv-value inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: r.type?.color }} />{r.type?.name}</span>
            </div>
            <div className="m-kv">
              <span className="m-kv-label">Perioadă</span>
              <span className="m-kv-value">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
            </div>
            <div className="m-kv">
              <span className="m-kv-label">{stage === "cancel" ? "Motiv anulare" : "Motiv"}</span>
              <span className="m-kv-value text-ink-muted font-normal line-clamp-2">{stage === "cancel" ? (r.cancellation_reason || r.reason || "—") : (r.reason || "—")}</span>
            </div>
            <div className="m-actions">
              <button onClick={() => onDetail(r)} className="border border-neutral-300 text-ink">Detalii</button>
              <button disabled={busy === r.id} data-testid={`btn-approve-${stage}-${r.id}-m`}
                onClick={() => onDecide(r, stage, "approve")} className="bg-emerald-600 text-white">
                <CheckCircle2 className="h-3.5 w-3.5" /> {approveLabel}
              </button>
              <button disabled={busy === r.id} data-testid={`btn-reject-${stage}-${r.id}-m`}
                onClick={() => onReject({ req: r, stage })} className="bg-red-600 text-white">
                <XCircle className="h-3.5 w-3.5" /> {rejectLabel}
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="surface overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left">
              <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Angajat</th>
              <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Tip</th>
              <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Perioadă</th>
              <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Zile</th>
              <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">
                {stage === "cancel" ? "Motiv anulare" : "Motiv"}
              </th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-b border-neutral-100">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-brand/10 text-brand flex items-center justify-center">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium">{r.employee?.name}</div>
                      <div className="text-xs text-ink-muted">{r.employee?.job_title}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2" style={{ background: r.type?.color }} />
                    <span>{r.type?.name}</span>
                  </div>
                </td>
                <td className="p-3 text-xs">{fmtDate(r.start_date)}<br />→ {fmtDate(r.end_date)}</td>
                <td className="p-3 font-mono">{r.business_days}</td>
                <td className="p-3 text-xs text-ink-muted max-w-[220px] truncate">
                  {stage === "cancel" ? (r.cancellation_reason || r.reason || "—") : (r.reason || "—")}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => onDetail(r)}
                      className="px-3 py-1.5 text-xs border border-neutral-300 hover:border-brand"
                    >
                      Detalii
                    </button>
                    <button
                      disabled={busy === r.id}
                      data-testid={`btn-approve-${stage}-${r.id}`}
                      onClick={() => onDecide(r, stage, "approve")}
                      className="px-3 py-1.5 text-xs bg-emerald-600 text-white hover:opacity-90 inline-flex items-center gap-1"
                    >
                      <CheckCircle2 className="h-3 w-3" /> {approveLabel}
                    </button>
                    <button
                      disabled={busy === r.id}
                      data-testid={`btn-reject-${stage}-${r.id}`}
                      onClick={() => onReject({ req: r, stage })}
                      className="px-3 py-1.5 text-xs bg-red-600 text-white hover:opacity-90 inline-flex items-center gap-1"
                    >
                      <XCircle className="h-3 w-3" /> {rejectLabel}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
